import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import SkipToContent from "@/components/SkipToContent";

const AccessibilityPage = () => {
  return (
    <>
      <Helmet>
        <title>הצהרת נגישות | אמון בזוגיות</title>
        <meta
          name="description"
          content="הצהרת נגישות של אתר אמון בזוגיות בהתאם לתקן הישראלי 5568 ו-WCAG 2.1 Level AA"
        />
      </Helmet>

      <SkipToContent />

      <div className="min-h-screen bg-background" dir="rtl">
        {/* Header */}
        <header role="banner" className="bg-navy py-4">
          <div className="container mx-auto px-4 flex justify-between items-center">
            <Logo variant="light" />
            <Link
              to="/"
              className="text-cream hover:text-gold transition-colors"
              aria-label="חזרה לדף הבית"
            >
              חזרה לדף הבית
            </Link>
          </div>
        </header>

        {/* Main Content */}
        <main id="main-content" role="main" tabIndex={-1} className="container mx-auto px-4 py-16 max-w-4xl">
          <h1 className="text-4xl font-bold text-navy mb-8">הצהרת נגישות</h1>

          <div className="prose prose-lg max-w-none space-y-8 text-foreground">
            <p className="text-muted-foreground">
              עודכן לאחרונה: {new Date().toLocaleDateString("he-IL")}
            </p>

            <section aria-labelledby="commitment-heading" className="space-y-4">
              <h2 id="commitment-heading" className="text-2xl font-semibold text-navy">
                מחויבותנו לנגישות
              </h2>
              <p>
                אמון בזוגיות מחויבת להנגשת האתר לאנשים עם מוגבלויות. אנו שואפים לעמוד
                בדרישות תקן הנגישות הישראלי 5568 המבוסס על הנחיות WCAG 2.1 ברמה AA.
              </p>
            </section>

            <section aria-labelledby="features-heading" className="space-y-4">
              <h2 id="features-heading" className="text-2xl font-semibold text-navy">
                תכונות הנגישות באתר
              </h2>
              <ul className="list-disc list-inside space-y-2 mr-4">
                <li>ניווט מלא באמצעות מקלדת</li>
                <li>תמיכה בקוראי מסך</li>
                <li>מבנה סמנטי תקני</li>
                <li>ניגודיות צבעים מספקת</li>
                <li>טקסט חלופי לתמונות</li>
                <li>תוויות לטפסים</li>
                <li>קישור "דלג לתוכן" בכל עמוד</li>
                <li>ווידג'ט נגישות עם אפשרויות התאמה:</li>
                <ul className="list-disc list-inside space-y-1 mr-8">
                  <li>הגדלה והקטנה של גודל הגופן</li>
                  <li>מצב ניגודיות גבוהה</li>
                  <li>הדגשת קישורים</li>
                  <li>עצירת אנימציות</li>
                </ul>
              </ul>
            </section>

            <section aria-labelledby="tech-heading" className="space-y-4">
              <h2 id="tech-heading" className="text-2xl font-semibold text-navy">
                טכנולוגיות נגישות
              </h2>
              <p>האתר בנוי בטכנולוגיות מתקדמות התומכות בנגישות:</p>
              <ul className="list-disc list-inside space-y-2 mr-4">
                <li>HTML5 סמנטי</li>
                <li>ARIA (Accessible Rich Internet Applications)</li>
                <li>CSS עם תמיכה בהעדפות משתמש (prefers-reduced-motion)</li>
              </ul>
            </section>

            <section aria-labelledby="limitations-heading" className="space-y-4">
              <h2 id="limitations-heading" className="text-2xl font-semibold text-navy">
                מגבלות ידועות
              </h2>
              <p>
                אנו עובדים באופן מתמיד לשיפור הנגישות. אם נתקלתם בבעיית נגישות, אנא
                פנו אלינו ונטפל בה בהקדם.
              </p>
            </section>

            <section aria-labelledby="contact-heading" className="space-y-4">
              <h2 id="contact-heading" className="text-2xl font-semibold text-navy">
                יצירת קשר בנושא נגישות
              </h2>
              <p>
                לכל שאלה, הערה או בקשה בנושא נגישות, ניתן ליצור קשר באמצעות:
              </p>
              <ul className="list-disc list-inside space-y-2 mr-4">
                <li>כפתור וואטסאפ באתר</li>
                <li>טופס יצירת הקשר בעמודי האתר</li>
              </ul>
              <p>
                אנו מתחייבים להשיב לפניות נגישות תוך 5 ימי עסקים.
              </p>
            </section>

            <section aria-labelledby="standard-heading" className="space-y-4">
              <h2 id="standard-heading" className="text-2xl font-semibold text-navy">
                התאמה לתקנים
              </h2>
              <p>
                האתר מותאם לדרישות:
              </p>
              <ul className="list-disc list-inside space-y-2 mr-4">
                <li>תקן ישראלי 5568 לנגישות אתרי אינטרנט</li>
                <li>WCAG 2.1 ברמה AA</li>
                <li>חוק שוויון זכויות לאנשים עם מוגבלויות (התשנ"ח-1998)</li>
              </ul>
            </section>
          </div>
        </main>

        {/* Footer */}
        <footer role="contentinfo" className="bg-navy text-cream py-8 mt-16">
          <div className="container mx-auto px-4 text-center space-y-4">
            <p>© {new Date().getFullYear()} אמון בזוגיות. כל הזכויות שמורות.</p>
            <nav aria-label="קישורי פוטר">
              <ul className="flex justify-center gap-6 text-sm">
                <li>
                  <Link to="/privacy" className="hover:text-gold transition-colors">
                    מדיניות פרטיות
                  </Link>
                </li>
                <li>
                  <Link to="/accessibility" className="hover:text-gold transition-colors" aria-current="page">
                    הצהרת נגישות
                  </Link>
                </li>
              </ul>
            </nav>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AccessibilityPage;
